import { memo } from "react";

import { Icon, type IconName } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import { type FooterSectionSettings } from "@react/sections/Footer/Footer.data";
import { type MarketIsoCode } from "@root/tablescape/constants";
import { CartHeader } from "@sections/Cart/CartHeader";
import { type Localization } from "@sections/DrawerProvider/CountrySelector";
import { type TTailwindString, tw } from "classnames";

const styles = {
  container: tw("bg-background-primary", "h-dynamic-screen"),
  header: tw("flex", "flex-row", "flex-nowrap", "items-center", "h-6"),
  drawerBody: tw("p-6", "overflow-y-auto", "text-primary"),
  mutedText: tw("text-primary-muted"),
  optionsHeader: tw(
    "border",
    "border-0",
    "border-b",
    "border-solid",
    "border-interactive-disabled-pressed",
    "pb-6"
  ),
  optionDisplay: tw("px-4", "flex", "flex-row", "flex-nowrap", "items-center"),
  country: tw("flex-1", "pl-2.5"),
  language: tw("text-char-500"),
  availableOption: tw(
    "w-full",
    "text-left",
    "cursor-pointer",
    "bg-transparent",
    "!px-0" as TTailwindString,
    "py-4",
    "border",
    "border-0",
    "border-b",
    "border-solid",
    "border-interactive-disabled-pressed",
    "no-underline",
    "text-primary"
  ),
  selectedOption: tw("mt-4", "mb-8", "py-3", "bg-background-secondary"),
};

export const COUNTRY_FLAG_ICONS: Record<MarketIsoCode, IconName> = {
  AU: "flag-australia",
  CA: "flag-canada",
  GB: "flag-united-kingdom",
  US: "flag-united-states",
  // FR: "flag-france",
};

type Props = {
  closeSelector: () => void;
  localization: Localization;
  localizationOptions: Localization[];
  text: FooterSectionSettings["countrySelector"]["text"];
};

const CountrySelectorViewBase = ({
  closeSelector,
  localization,
  localizationOptions,
  text,
}: Props) => {
  const localizationOptionGenerator = (option: Localization) => {
    return (
      <>
        <Icon name={COUNTRY_FLAG_ICONS[option.isoCode]} />
        <Text as="body1" className={styles.country} size={{ md: "md" }}>
          {option.country}
        </Text>
        <Text as="body1" className={styles.language} size={{ md: "md" }}>
          {option.language}
        </Text>
      </>
    );
  };
  return (
    <div className={styles.container}>
      <CartHeader handleCloseDrawer={closeSelector}>{text.header}</CartHeader>

      <div className={styles.drawerBody}>
        <Text className={styles.mutedText}>{text.location}</Text>
        {localization?.isoCode && (
          <div className={tw(styles.selectedOption, styles.optionDisplay)}>
            {localizationOptionGenerator(localization)}
          </div>
        )}
        <div className={styles.optionsHeader}>
          <Text as="body0">{text.label}</Text>
          <Text className={styles.mutedText}>{text.shipping}</Text>
        </div>
        {localizationOptions.map((option: Localization) => (
          <a
            className={tw(styles.availableOption, styles.optionDisplay)}
            href={`https://${option.domain}`}
            key={option.country}
          >
            {localizationOptionGenerator(option)}
          </a>
        ))}
      </div>
    </div>
  );
};

export const CountrySelectorView = memo(CountrySelectorViewBase);
